import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fallbackTestimonials = [
  {
    id: '1',
    name: 'Sarah Mitchell',
    role: 'Corporate Events Manager, TechCorp SA',
    quote:
      'NewCreative transformed our annual gala into an unforgettable experience. The lighting, sound, and entertainment were absolutely world-class.',
    rating: 5,
  },
  {
    id: '2',
    name: 'James van der Berg',
    role: 'Wedding Planner',
    quote:
      "I've worked with many event production companies, but NewCreative's attention to detail and professionalism is unmatched. My clients always ask for them.",
    rating: 5,
  },
  {
    id: '3',
    name: 'Thandi Mokoena',
    role: 'Marketing Director, Luxe Brands',
    quote:
      'The LED screens and special effects at our product launch created an immersive brand experience that our guests are still talking about months later.',
    rating: 5,
  },
];

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        if (!BACKEND_URL) return;
        const response = await axios.get(`${API}/testimonials`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (error) {
        console.log('Using fallback testimonials');
      }
    };
    fetchTestimonials();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const current = testimonials[currentIndex];

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
          >
            Client Stories
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111]"
          >
            What Our Clients <span className="text-[#2A93A6]">Say</span>
          </motion.h2>
        </div>

        {/* Testimonial Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <div
            className="testimonial-card relative p-8 md:p-12 rounded-lg"
            data-testid="testimonial-card"
            aria-live="polite"
          >
            {/* Quote Icon */}
            <Quote
              size={48}
              className="absolute top-8 left-8 text-[#47c3d6]/20"
            />

            {/* Stars */}
            <span className="sr-only">Rated {current.rating} out of 5</span>
            <div className="flex gap-1 mb-6 justify-center" aria-hidden="true">
              {[...Array(current.rating)].map((_, i) => (
                <Star
                  key={i}
                  size={20}
                  className="fill-[#2A93A6] text-[#2A93A6]"
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-xl md:text-2xl text-[#111111] text-center leading-relaxed mb-8 relative z-10">
              "{current.quote}"
            </blockquote>

            {/* Author */}
            <div className="text-center">
              <p className="text-[#111111] font-bold text-lg">{current.name}</p>
              <p className="text-[#666666] text-sm">{current.role}</p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
              className="w-12 h-12 bg-[#F5F8FA] hover:bg-[#47c3d6]/10 border border-[#E5E9ED] hover:border-[#47c3d6] rounded-full flex items-center justify-center transition-all duration-300"
            >
              <ChevronLeft size={20} className="text-[#444444]" aria-hidden="true" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-current={index === currentIndex ? 'true' : undefined}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-[#2A93A6] w-6'
                      : 'bg-[#949494] hover:bg-[#666666]'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              data-testid="testimonial-next"
              aria-label="Next testimonial"
              className="w-12 h-12 bg-[#F5F8FA] hover:bg-[#47c3d6]/10 border border-[#E5E9ED] hover:border-[#47c3d6] rounded-full flex items-center justify-center transition-all duration-300"
            >
              <ChevronRight size={20} className="text-[#444444]" aria-hidden="true" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
